import { useCallback, useEffect, useRef, useState } from "react";
import useEstadoForm from "./AddressForm/useEstadoForm";
import useCidadeForm from "./AddressForm/useCidadeForm";

import useCeps from "~/fetchs/useCeps";
import { buscarRuasPorCidadeId } from "~/fetchs/useRuas";

const useAddressForm = (formikRef, dataComingBack, validarCepOperacional = true) => {
  const [codigoPostal, setCodigoPostal] = useState("");
  const initialHydratedRef = useRef(false);
  const returningDataHydratedRef = useRef(false);
  const [isCepModalOpen, setIsCepModalOpen] = useState(false);
  const [ruasExOperacional, setRuasExOperacional] = useState([]);
  const [cepPendente, setCepPendente] = useState(false);

  const { estadoSelect, estados, handleChangeUf, loadingEstado } = useEstadoForm(formikRef);

  const { cidadeSelect, cidades, handleChangeCidade, loadingCidade, fillCidadeById } = useCidadeForm(
    formikRef,
    estadoSelect?.value?.value
  );

  const handleError = (err) => {
    console.error(err);
    formikRef?.current?.setErrors({ ...formikRef?.current?.errors, cep: "CEP não encontrado" });
  };

  const { cep, loading } = useCeps({ codigoPostal, handleError });

  const touchField = (field) => {
    formikRef?.current?.setFieldTouched(field, true, false);
  };

  const fillCEP = ({ logradouro, bairro, cidadeId, sigla }) => {
    try {
      const currentValues = formikRef?.current?.values || {};

      if (logradouro || !currentValues.rua) {
        formikRef?.current?.setFieldValue("rua", logradouro ?? "", false);
        touchField("rua");
      }

      if (bairro || !currentValues.bairro) {
        formikRef?.current?.setFieldValue("bairro", bairro ?? "", false);
        touchField("bairro");
      }

      const uf = estados.find((e) => e.value == sigla);

      if (uf) handleChangeUf(uf);

      fillCidadeById(cidadeId);
    } catch (error) {
      console.error("Erro ao preencher os campos via CEP", error);
    }
  };

  const verificarCep = async (cepInfo) => {
    if (!validarCepOperacional) return false;

    const cidadeId = cepInfo?.cidadeId;
    if (!cidadeId) return false;

    try {
      const ruas = await buscarRuasPorCidadeId(cidadeId);
      const cidadeTemVariasRuas = ruas && ruas.length > 1;
      const isCepGenerico = !cepInfo?.logradouro;
      const isExOperacional = cidadeTemVariasRuas && isCepGenerico;

      if (isExOperacional) {
        const cepConsultado = String(cepInfo?.cep ?? "").replace(/\D/g, "");
        const cepAtual = String(formikRef?.current?.values?.cep ?? "").replace(/\D/g, "");
        if (cepConsultado && cepAtual && cepConsultado !== cepAtual) return false;

        formikRef?.current?.setFieldValue("rua", "", false);
        formikRef?.current?.setFieldValue("bairro", "", false);
        formikRef?.current?.setFieldValue("numero", "", false);
        formikRef?.current?.setFieldValue("complemento", "", false);

        setRuasExOperacional(ruas);
        setIsCepModalOpen(true);
        setCepPendente(true);
      } else {
        setCepPendente(false);
        setIsCepModalOpen(false);
      }

      return isExOperacional;
    } catch (error) {
      console.error(error);
      setCepPendente(false);
      return false;
    }
  };

  const handleSelecionarRua = (rua) => {
    const cepNormalizado = String(rua.cep ?? "").replace(/\D/g, "");
    const cepFormatado = cepNormalizado.replace(/^(\d{5})(\d{3})$/, "$1-$2");

    formikRef?.current?.setFieldValue("cep", cepFormatado, false);
    formikRef?.current?.setFieldValue("rua", rua.logradouro ?? rua.rua ?? "", false);
    formikRef?.current?.setFieldValue("bairro", rua.bairro ?? "", false);
    formikRef?.current?.setFieldError("cep", undefined);
    touchField("cep");
    touchField("rua");
    touchField("bairro");

    setCepPendente(false);
    setIsCepModalOpen(false);
  };

  const handleFecharModalSemSelecionar = () => {
    setIsCepModalOpen(false);
    setCodigoPostal("");
  };

  useEffect(() => {
    if (!validarCepOperacional) {
      setIsCepModalOpen(false);
      setCepPendente(false);
    }
  }, [validarCepOperacional]);

  const handleCepOnChange = async (cep, onFocus) => {
    if (onFocus) return;
    cep = cep.replace(/[^0-9]/g, "");
    setCodigoPostal(cep);
  };

  const handleCepOnBlur = async (cep) => {
    cep = cep.replace(/[^0-9]/g, "");
    if (cep?.length == 8) {
      setCodigoPostal(cep);
    } else handleCepError();
  };

  const handleCepError = () => {
    handleChangeUf({ label: "", value: null });
    handleChangeCidade({ label: "", value: null });

    formikRef?.current?.setFieldValue("rua", "", false);
    touchField("rua");

    formikRef?.current?.setFieldValue("numero", "", false);
    touchField("numero");

    formikRef?.current?.setFieldValue("complemento", "", false);
    touchField("complemento");

    formikRef?.current?.setFieldValue("bairro", "", false);
    touchField("bairro");

    setCepPendente(false);
    setIsCepModalOpen(false);
  };

  useEffect(() => {
    if (Object.keys(cep).length > 0) {
      fillCEP(cep);
      verificarCep(cep);
    }
  }, [cep]);

  //  PREENCHIMENTO INICIAL DAS TELAS DE EDIÇÃO
  useEffect(() => {
    if (initialHydratedRef.current) return;
    if (formikRef?.current?.initialValues?.cep) {
      let ufLabel = formikRef?.current?.values?.cidade?.estado?.nome;
      let ufSigla = formikRef?.current?.values?.cidade?.estado?.sigla;

      let cidadeLabel = formikRef?.current?.values?.cidade?.nome;
      let cidadeId = formikRef?.current?.values?.cidade?.id;

      if (formikRef?.current?.initialValues?.endereco?.cep && !ufLabel) {
        ufLabel = formikRef?.current?.initialValues?.endereco?.cidade?.estado?.nome;
        ufSigla = formikRef?.current?.initialValues?.endereco?.cidade?.estado?.sigla;

        cidadeLabel = formikRef?.current?.initialValues?.endereco?.cidade?.nome;
        cidadeId = formikRef?.current?.initialValues?.endereco?.cidade?.id;
      }

      if (!ufSigla && !cidadeId) return;

      handleChangeUf({ label: ufLabel, value: ufSigla  });
      handleChangeCidade({ label: cidadeLabel, value: cidadeId });
      initialHydratedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formikRef?.current?.initialValues?.cep,
    formikRef?.current?.values?.cidade?.id,
    formikRef?.current?.initialValues?.endereco?.cidade?.id,
  ]);

  useEffect(() => {
    if (returningDataHydratedRef.current) return;
    if (dataComingBack?.estado) {
      const estadoObj = dataComingBack.estado || {};
      const cidadeObj = dataComingBack.cidade || {};

      const ufLabel = estadoObj.nome;
      const ufSigla = estadoObj.sigla;

      const cidadeLabel = cidadeObj.nome;
      const cidadeId = cidadeObj.id;

      handleChangeUf({ label: ufLabel, value: ufSigla });
      handleChangeCidade({ label: cidadeLabel, value: cidadeId });
      returningDataHydratedRef.current = true;
    }
  }, [dataComingBack, handleChangeUf, handleChangeCidade, formikRef]);

  useEffect(() => {
    const estadosLoaded = (estados || []).length > 0;
    if (!estadosLoaded) return;

    const estadoObj = dataComingBack?.estado;
    const cidadeObj = dataComingBack?.cidade;

    const formikInitCep = formikRef?.current?.initialValues?.endereco?.cep;

    if (!formikRef?.current) return;

    const currentUf = formikRef.current.values?.uf;
    const currentCidadeId = formikRef.current.values?.cidadeId;


    if (estadoObj && estadoObj.sigla && currentUf !== estadoObj.sigla) {
      handleChangeUf({ label: estadoObj.nome, value: estadoObj.sigla });
      if (cidadeObj?.id) {
        fillCidadeById(cidadeObj.id);
      }
      return;
    }

    if (formikInitCep && formikRef.current.initialValues?.endereco && currentCidadeId !== formikRef.current.initialValues.endereco.cidade?.id) {
      const initCidade = formikRef.current.initialValues.endereco.cidade || {};
      const initEstado = formikRef.current.initialValues.endereco.cidade?.estado || {};
      if (initEstado?.sigla) {
        handleChangeUf({ label: initEstado.nome, value: initEstado.sigla });
      }
      if (initCidade?.id) fillCidadeById(initCidade.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estados?.length, dataComingBack, formikRef, formikRef?.current?.initialValues?.endereco?.cidade?.id]);

  return {
    verificarCep,
    fillCEP,
    handleCepOnBlur,
    handleCepOnChange,
    handleSelecionarRua,
    handleFecharModalSemSelecionar,
    cepModal: {
      isOpen: isCepModalOpen,
      ruas: ruasExOperacional,
    },
    cepPendente,
    cep: {
      loading: loading,
    },
    cidade: {
      loading: loadingCidade,
      options: cidades,
      select: cidadeSelect,
      handleChange: handleChangeCidade,
    },
    estado: {
      loading: loadingEstado,
      options: estados,
      select: estadoSelect,
      handleChange: handleChangeUf,
    },
  };
};

export default useAddressForm;
